/* PasswordResetSectionRenderer.js */

"use strict";
const InputPasswordControl = require("@gluenetworks/steve_ui_core").Control.Textfield.InputPassword;
const ButtonControl = require("@gluenetworks/steve_ui_core").Control.Action.Button;

class PasswordResetSectionRenderer {
    constructor(model) {
        this._model = model;
        this._rootElem = document.createElement("password-reset-section-gl");
        this._rootElem.component = this._model;

        this._contentContainer = document.createElement("content-container");

        this._formControls = document.createElement("form-controls");

        this._resetLabel = document.createElement("h2");
        this._resetLabel.innerText = "Password Reset";

        this._passwordInput = new InputPasswordControl({value: "", placeholder: "Password"});
        this._confirmPasswordInput = new InputPasswordControl({
            value: "",
            placeholder: "Confirm Password",
        });
        this._confirmPasswordError = document.createElement("p");
        this._confirmPasswordError.style.display = "none";
        this._confirmPasswordError.classList.add("error");
        this._buttonContainer = document.createElement("button-container");
        this._resetButton = new ButtonControl({
            darkTheme: true,
            label: "Reset Password",
            disabled: false,
        });

        this._successContainer = document.createElement("success-container");

        this._successHeader = document.createElement("h2");
        this._successHeader.innerText = "Successful Password Reset";

        this._successLoader = document.createElement("base-icon-gl");
        this._successLoader.dataset.icon = "common.loading";
        this._successLoader.setAttribute("data-dark-theme", "true");

        this._successMessage = document.createElement("p");
        this._successMessage.innerHTML = `Redirecting you to Gluware Control <a tabindex="-1" href="/">login</a>`;
    }

    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */

    /********************************************/
    _registerListeners() {
        this._model.on("busy", (this._onBusyStatusChange = this._onBusyStatusChange.bind(this)));
        this._model.on(
            "passwordValid",
            (this._onPasswordValidation = this._onPasswordValidation.bind(this))
        );
        this._model.on("passwordError", (this._onPasswordError = this._onPasswordError.bind(this)));
        this._model.on(
            "passwordChangeSuccess",
            (this._onPasswordChangeSuccess = this._onPasswordChangeSuccess.bind(this))
        );

        this._resetButton.on(
            "UserClick",
            (this._onResetButtonClick = this._onResetButtonClick.bind(this))
        );
        this._passwordInput.on(
            "UserValue",
            (this._onPassword1Change = this._onPassword1Change.bind(this))
        );
        this._confirmPasswordInput.on(
            "UserValue",
            (this._onPassword2Change = this._onPassword2Change.bind(this))
        );
    }

    _unregisterListeners() {
        this._model.removeListener("busy", this._onBusyStatusChange);
        this._model.removeListener("passwordValid", this._onPasswordValidation);
        this._model.removeListener("passwordError", this._onPasswordError);
        this._model.removeListener("passwordChangeSuccess", this._onPasswordChangeSuccess);

        this._resetButton.removeListener("UserClick", this._onResetButtonClick);
        this._passwordInput.removeListener("UserValue", this._onPassword1Change);
        this._confirmPasswordInput.removeListener("UserValue", this._onPassword2Change);
    }

    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */

    /*******************************************/
    shutdown() {
        return Promise.resolve()
            .then(() => {
                this._unregisterListeners();

                return Promise.all([
                    this._passwordInput.shutdown(),
                    this._confirmPasswordInput.shutdown(),
                    this._resetButton.shutdown(),
                ]);
            })
            .then(() => {
                this._contentContainer = undefined;
                this._formControls = undefined;
                this._resetLabel = undefined;

                this._passwordInput = undefined;
                this._confirmPasswordInput = undefined;
                this._confirmPasswordError = undefined;
                this._buttonContainer = undefined;
                this._resetButton = undefined;

                this._successContainer = undefined;
                this._successHeader = undefined;
                this._successLoader = undefined;
                this._successMessage = undefined;

                this._model = undefined;
                this._rootElem.remove();
                this._rootElem = undefined;
            })
            .catch((err) => console.log("Error shutting down PasswordResetSectionRenderer: ", err));
    }

    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */
    render() {
        return Promise.resolve()
            .then(() => {
                return this._model.setSelectedTenantId();
            })
            .then(() => {
                this._registerListeners();

                if (this._rootElem.hasChildNodes()) {
                    return;
                }

                this._rootElem.appendChild(this._contentContainer);

                this._contentContainer.appendChild(this._formControls);
                this._formControls.appendChild(this._resetLabel);
                this._formControls.appendChild(this._passwordInput.renderer.rootElement);
                this._formControls.appendChild(this._confirmPasswordInput.renderer.rootElement);
                this._formControls.appendChild(this._confirmPasswordError);
                this._formControls.appendChild(this._buttonContainer);
                this._buttonContainer.appendChild(this._resetButton.renderer.rootElement);

                this._successContainer.appendChild(this._successHeader);
                this._successContainer.appendChild(this._successLoader);
                this._successContainer.appendChild(this._successMessage);
            });
    }

    get rootElement() {
        return this.getRootElement();
    }

    getRootElement() {
        return this._rootElem;
    }

    get model() {
        return this.getModel();
    }

    getModel() {
        return this._model;
    }

    /*******************************************/
    /* END COMPONENT RENDERER METHODS */
    /* START PRIVATE RENDER METHODS */
    /*******************************************/
    // Private Render Methods here
    /*******************************************/
    /* END PRIVATE RENDER METHODS */
    /* START HANDLER METHODS */

    /*******************************************/

    _onResetButtonClick() {
        this._model.resetUserPassword(
            this._passwordInput.getValue(),
            this._confirmPasswordInput.getValue()
        );
    }

    _onPassword1Change(value) {
        this._model.onPasswordChange(value, this._confirmPasswordInput.getValue());
    }

    _onPassword2Change(value) {
        this._model.confirmPasswordDirty = true;
        this._model.onPasswordChange(this._passwordInput.getValue(), value);
    }

    _onBusyStatusChange(isBusy) {
        if (isBusy) {
            this._passwordInput.setDisabled(true);
            this._confirmPasswordInput.setDisabled(true);
            this._resetButton.setDisabled(true);
        } else {
            this._passwordInput.setDisabled(false);
            this._confirmPasswordInput.setDisabled(false);
            this._resetButton.setDisabled(false);
        }
    }

    _onPasswordValidation(isValid, errMsg) {
        if (errMsg) {
            this._confirmPasswordError.innerText = errMsg;
            this._confirmPasswordError.style.display = "block";
        } else {
            this._confirmPasswordError.style.display = "none";
        }

        if (isValid) {
            this._confirmPasswordError.style.display = "none";
            this._confirmPasswordInput.setInvalid(false);
            this._resetButton.setDisabled(false);
        } else {
            this._resetButton.setDisabled(true);
            this._confirmPasswordInput.setInvalid(true);
        }
    }

    _onPasswordError(errMsg) {
        if (errMsg) {
            this._confirmPasswordError.innerText = errMsg;
            this._confirmPasswordError.style.display = "block";
            this._passwordInput.setDisabled(false);
            this._confirmPasswordInput.setDisabled(false);
            this._resetButton.setDisabled(false);
        }
    }

    _onPasswordChangeSuccess() {
        this._contentContainer.removeChild(this._formControls);
        this._contentContainer.appendChild(this._successContainer);
    }

    /*******************************************/
    /* END HANDLER METHODS */
    /* START PRIVATE API METHODS */
    /*******************************************/
    // Nobody should be using methods on a Renderer directly so make all these methods private
    /*******************************************/
    /*  END PRIVATE API METHODS */
    /*******************************************/
}

module.exports = PasswordResetSectionRenderer;
